import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthApi } from '../../../hooks/useAuthApi';

interface AuthUser {
  id: string;
  email: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  tokenExpiry: Date | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  signup: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<boolean>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem('token') !== null;
  });
  const [user, setUser] = useState<AuthUser | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('token');
  });
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(() => {
    const expiryStr = localStorage.getItem('tokenExpiry');
    return expiryStr ? new Date(expiryStr) : null;
  });
  const [loading, setLoading] = useState(false);

  const { login: apiLogin, logout: apiLogout, signup: apiSignup } = useAuthApi();

  const login = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    const result = await apiLogin(email, password);
    setLoading(false);

    if (result.status === 'success' && result.data) {
      setIsAuthenticated(true);
      setUser(result.data.user);
      setToken(result.data.token);
      setTokenExpiry(new Date(result.data.expires));

      localStorage.setItem('user', JSON.stringify(result.data.user));
      localStorage.setItem('token', result.data.token);
      localStorage.setItem('tokenExpiry', result.data.expires);

      return { success: true, message: 'Login successful' };
    } else {
      return { success: false, message: result.message || 'Login failed' };
    }
  };

  const signup = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    const response = await apiSignup(email, password);
    setLoading(false);

    if (response.success && response.data) {
      setIsAuthenticated(true);
      setUser(response.data.user);
      setToken(response.data.token);
      setTokenExpiry(new Date(response.data.expires));

      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('tokenExpiry', response.data.expires);

      return { success: true, message: 'Signup successful' };
    }

    return { success: response.success, message: response.message };
  };

  const logout = async (): Promise<void> => {
    await apiLogout();
    // Clear all auth data
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');

    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setTokenExpiry(null);
  };

  // Method to check if user is authenticated and token is not expired
  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      if (!token || !tokenExpiry) {
        return false;
      }

      // Check if token is expired
      const now = new Date();
      if (now > tokenExpiry) {
        // Token expired, clear auth data
        await logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking auth status:', error);
      return false;
    }
  };

  // Check authentication status when the component mounts
  useEffect(() => {
    checkAuthStatus();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        tokenExpiry,
        login,
        signup,
        logout,
        checkAuthStatus,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
